import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import ScopeField from '../../components/ScopeField';
import { fetchTerminalReport } from '../../api/terminalReport';
import { downloadCSV } from '../../utils/csv';
import { bankLabel } from '../../utils/bank';
import './TerminalReportPage.css';

const ORDER_FIELD_OPTIONS = ['Terminal', 'Nom', 'Banque', 'Superviseur'];
const DEFAULT_ORDER = [
  { field: 'Terminal', dir: 'Asc' },
  { field: 'Nom', dir: 'Asc' },
  { field: 'Banque', dir: 'Asc' },
  { field: 'Superviseur', dir: 'Asc' },
];

const filterApiValue = (filter) => {
  if (filter === 'Groupe') return 'Group';
  if (filter === 'Superviseur') return 'Supervisor';
  return 'All';
};

const TerminalReportPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [order, setOrder] = useState(DEFAULT_ORDER);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState(null);

  const updateOrder = (i, field, value) => {
    setOrder((prev) => prev.map((o, idx) => (idx === i ? { ...o, [field]: value } : o)));
  };

  const handleDownload = () => {
    if (!rows || rows.length === 0) return;
    const header = ['Terminal', 'Type', 'Nom', 'Code', 'Banque Nom', 'Adresse', 'Superviseur'];
    const dataRows = rows.map((row) => [
      row.terminal, row.type, row.nom, row.code, bankLabel(row.banque_seq, row.banque_nom), row.adresse, row.superviseur,
    ]);
    downloadCSV('terminal-rapport.csv', [
      ['Terminal Rapport'],
      [],
      header,
      ...dataRows,
    ]);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await fetchTerminalReport({
        filter: filterApiValue(filter),
        scope: filter === 'Tout' ? undefined : scopeId,
        orderBy: order,
      });
      setRows(data);
    } catch (err) {
      setError('Recherche impossible.');
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <p className="tr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Reports</Link>
        {' >> '}
        <span className="bc-current">Terminals Rapport</span>
      </p>

      <div className="tr-card">
        <div className="tr-card-header">
          <span className="tr-card-title">Terminals Rapport</span>
        </div>
        <div className="tr-card-body">
          <div className="tr-radio-row">
            {['Tout', 'Groupe', 'Superviseur'].map((opt) => (
              <label key={opt} className="tr-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="tr-row tr-scope-row">
              <label className="tr-label">{filter}</label>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="tr-input tr-input-sm"
                iconClass="tr-icon-btn"
                nameClass="tr-copy-name"
              />
            </div>
          )}

          <form onSubmit={handleSearch}>
            <p className="tr-section-title">Commande par</p>
            {order.map((o, i) => (
              <div key={i} className="tr-order-row">
                <select
                  className="tr-select tr-select-field"
                  value={o.field}
                  onChange={e => updateOrder(i, 'field', e.target.value)}
                >
                  {ORDER_FIELD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <select
                  className="tr-select tr-select-dir"
                  value={o.dir}
                  onChange={e => updateOrder(i, 'dir', e.target.value)}
                >
                  <option value="Asc">Asc</option>
                  <option value="Desc">Desc</option>
                </select>
              </div>
            ))}

            {error && <p className="tr-error">{error}</p>}

            <button type="submit" className="tr-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>
        </div>
      </div>

      {rows && (
        <div className="tr-card tr-results">
          <div className="tr-card-header tr-results-header">
            <span className="tr-card-title">Terminal Rapport</span>
            {rows.length > 0 && (
              <button type="button" className="tr-download-btn" onClick={handleDownload}>Télécharger</button>
            )}
          </div>
          <div className="tr-card-body">
            {rows.length === 0 ? (
              <p className="tr-empty">Aucun terminal sur cette portée.</p>
            ) : (
              <table className="tr-table">
                <thead>
                  <tr>
                    <th>Terminal</th>
                    <th>Type</th>
                    <th>Nom</th>
                    <th>Code</th>
                    <th>Banque Nom</th>
                    <th>Adresse</th>
                    <th>Superviseur</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.code}>
                      <td>{row.terminal || '—'}</td>
                      <td>{row.type}</td>
                      <td>{row.nom}</td>
                      <td>{row.code}</td>
                      <td>{bankLabel(row.banque_seq, row.banque_nom)}</td>
                      <td>{row.adresse || '.'}</td>
                      <td>{row.superviseur}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TerminalReportPage;
